import React from 'react';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import type { GlColumnDef } from '@/services/accountService/glInvoiceService';
import { resolveColumnLabelParts } from '@/pages/components/account/gl-invoice/columnAliasDisplay';

export interface GlColumnLabelProps {
    def: GlColumnDef;
    fallbackZh?: string;
    /** 是否显示客户别名行（页级开关或行级展开） */
    showAlias: boolean;
    /** 页级「Show client names」：开则不显示眼睛按钮 */
    pageShowAlias?: boolean;
    /** 有别名且页级未开时，显示眼睛按钮 */
    onToggleReveal?: () => void;
    /** 后缀，如 " @ 7.8" */
    suffix?: string;
    maxWidth?: number | string;
    lineHeight?: number | string;
    fontSize?: number;
}

/**
 * 标准 Item 名 + 可选彩色客户别名 + 眼睛按钮。
 */
export default function GlColumnLabel({
    def,
    fallbackZh,
    showAlias,
    pageShowAlias = false,
    onToggleReveal,
    suffix = '',
    maxWidth = 280,
    lineHeight = '22px',
    fontSize = 13,
}: GlColumnLabelProps) {
    const parts = resolveColumnLabelParts(def, fallbackZh);
    const showClient = showAlias && !!parts.client;
    const title = showClient
        ? `${parts.primary} · ${parts.client}${suffix}`
        : `${parts.primary}${suffix}`;
    const showEye = parts.hasAlias && !!onToggleReveal && !pageShowAlias;

    return (
        <Stack
            direction="row"
            alignItems="center"
            spacing={0.25}
            sx={{ maxWidth, minHeight: lineHeight }}
        >
            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize,
                    lineHeight,
                    height: showClient ? 'auto' : lineHeight,
                }}
                title={title}
            >
                <Typography
                    component="span"
                    sx={{ fontSize: 'inherit', lineHeight: 'inherit', color: 'text.primary' }}
                >
                    {parts.primary}
                </Typography>
                {suffix ? (
                    <Typography
                        component="span"
                        sx={{ fontSize: 'inherit', lineHeight: 'inherit', color: 'text.secondary' }}
                    >
                        {suffix}
                    </Typography>
                ) : null}
                {showClient && (
                    <Typography
                        component="span"
                        sx={{
                            display: 'block',
                            fontSize: Math.max(11, fontSize - 2),
                            lineHeight: 1.2,
                            color: 'info.main',
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        Client: {parts.client}
                    </Typography>
                )}
            </Box>
            {showEye && (
                <Tooltip title={showClient ? 'Hide client name' : 'Show client name'}>
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggleReveal?.();
                        }}
                        sx={{ p: 0.15, flexShrink: 0, color: showClient ? 'info.main' : 'action.active' }}
                    >
                        {showClient
                            ? <VisibilityOffIcon sx={{ fontSize: 14 }} />
                            : <VisibilityIcon sx={{ fontSize: 14 }} />}
                    </IconButton>
                </Tooltip>
            )}
        </Stack>
    );
}
